import React, { useState } from "react";
import './Graphic.css';

interface IGraphicProps{
    values: Array<number>;
    valuesX?: Array<number>;
    median?: number;
    mode?: 'line' | 'spline';
    maxValueY? : number;
    valueTitle?: string;
    showLast?: boolean;
    valueAlias?: Record<string, string>;
}

export const Graphic = ({values, valuesX, median, mode = "spline", maxValueY : _maxValueY, valueTitle = "", showLast = false, valueAlias}: IGraphicProps)=>{
    const [cursorPosition, setCursorPosition] = useState({x:0, y:0, vx: 0, vy: 0});
    const [over, setOver] = useState(false);
    const size = {x: 600, y: 200};
    const svgSize = {x: 600, y: 230};
    const linesCount = 5;
    const verticalLinesCount = 5;
    const linesData = new Array(linesCount).fill(0).map((it, i)=> (values.length / linesCount) * (i + 0.5) * size.x / values.length);
    const offsetScale = 1.1;
    const minValue = false ? 0 : values.reduce((ac, it) => Math.min(ac, it), Number.MAX_SAFE_INTEGER);
    const lastValue = values[values.length - 1] || 0;
    const maxValueY = (_maxValueY ?? lastValue) * 1.1;
    const verticalLinesData = new Array(verticalLinesCount).fill(0).map((it, i) => maxValueY * (i + 1) / verticalLinesCount);
    const scaleY = size.y / maxValueY;
    const minValueX = valuesX ? valuesX.reduce((ac, it)=>Math.min(ac, it), Number.MAX_SAFE_INTEGER) : 0;
    const maxValueX = valuesX ? valuesX.reduce((ac, it)=>Math.max(ac, it), Number.MIN_SAFE_INTEGER) : 1;
    const rangeX = maxValueX - minValueX || 1;
    const scaleX = size.x / rangeX;
    const pathData = values.map((it, i)=>{
        const ml = i==0 ? 'M' : 'L';
        const x = valuesX ? (valuesX[i] - minValueX) * scaleX : i* size.x / values.length;
        return `${ml}${x} ${size.y - it * scaleY}`;
    });
    const pathDataQ = values.map((it, i)=>{
        const x = valuesX ? (valuesX[i] - minValueX) * scaleX : i* size.x / values.length;
        const y = size.y - it * scaleY;
        if (i==0){
            return`${'M'}${i* size.x / values.length} ${size.y - it * scaleY}`;  
        }
        const prev = values[i-1];
        const next = values[i+1] ?? it;
        const prevX = valuesX ? (valuesX[i - 1] - minValueX) * scaleX : (i-1)* size.x / values.length;
        const nextX = valuesX ? (valuesX[i + 1] - minValueX) * scaleX : (i+1)* size.x / values.length;
        const prevY = size.y - prev * scaleY;
        const nextY = size.y - next * scaleY;
        return i==values.length-1 ? `Q${x} ${y}, ${(x)} ${(nextY + y) / 2}`: `C${x} ${y}, ${(nextX + x*3) / 4} ${(nextY + y*3) / 4}, ${(nextX*3 + x) / 4} ${(nextY*3 + y) / 4}`;
    });
    const pathValue = mode == "line" ? pathData.join(' ') : pathDataQ.join(' ');
    const pathValueFill = (pathValue) + ` L${size.x} ${size.y} L0 ${size.y} z`;

    if (!values || !values.length){
        return <div className={`graphic`}>
            no data
        </div>
    }

    return <div className={`graphic`}>
        <svg className="graphic_svg" width={svgSize.x} height={svgSize.y} viewBox={`0 0 ${svgSize.x} ${svgSize.y}`} fill="none" xmlns="http://www.w3.org/2000/svg" onMouseMove={(e)=>{
            const tw = new DOMPoint(e.nativeEvent.offsetX, e.nativeEvent.offsetY).matrixTransform(((e.target as SVGSVGElement).closest('.graphic_svg') as SVGSVGElement).getCTM().inverse());
            const vx = valuesX ? valuesX.findIndex(it=>(it - minValueX) * scaleX > tw.x) : Math.floor((tw.x / size.x) * values.length);
            const drawX = valuesX ? (valuesX[vx] - minValueX) * scaleX : vx * size.x / values.length;
            const drawY = size.y - values[vx] * scaleY;

            const rx = valuesX ? tw.x / scaleX + minValueX: (tw.x / size.x) * values.length;

            const interp = valuesX ? 
                (size.y - values[vx-1] * scaleY) * (Math.min((rx - valuesX[vx])/(valuesX[vx - 1] -valuesX[vx]), 1)) + (size.y - values[vx] * scaleY) * (1-Math.min((rx - valuesX[vx])/(valuesX[vx - 1] -valuesX[vx]), 1))
                :(size.y - values[vx] * scaleY) * (1-(rx-vx)) + (size.y - values[vx + 1] * scaleY) * ((rx - vx))
            let vy = interp;
            if ((vx >= values.length - 1)){
                vy = size.y - values[values.length - 1] * scaleY;
            }
            if (vx <= 0){
                if (Number.isNaN(vy)){
                    vy = size.y - values[0] * scaleY;
                }
            }
            setCursorPosition({
                x: tw.x,
                y: tw.y - 15,
                vx: valuesX ? (rx - minValueX) * scaleX : rx * size.x / values.length,
                vy: vy
            });
        }}
            onMouseEnter={()=>{
                setOver(true)
            }}
            onMouseLeave={()=>{
                setOver(false)
            }}  
            >
            <g transform="translate(0 15)">
                <circle cx={cursorPosition.vx} cy={cursorPosition.vy} r={3} stroke="#000"></circle>
                <path className="graphic_path" d={pathValue}></path>
                <path className="graphic_path_end" d={`M${size.x - size.x / values.length} ${size.y - values[values.length -1] * scaleY} L${size.x} ${size.y - values[values.length -1] * scaleY}`}></path>
                <path className="graphic_path_fill" d={pathValueFill} fill="url('#fillGradient')"></path>
                {median!=undefined && <path className="graphic_path_median" d={`M${0} ${size.y - median * scaleY} L${size.x} ${size.y - median * scaleY}`}></path>}
                <g>
                    {linesData.map((it, i)=>{
                    const dateValue = valuesX?.[Math.floor(i * values.length / linesCount)];
                    if (!dateValue || dateValue === 0 || Number.isNaN(dateValue)) {
                        return <g key={i}>
                            <text className="graphic_grid_text" x={it} y="0">--.--.--</text>
                            <path className="graphic_grid" d={`M${it} 0 L${it} ${size.y - 21}`} transform="translate(0 15)"></path>
                        </g>
                    }

                    // Check if this looks like session time (small numbers, typically < 1000)
                    // vs timestamps (large numbers, typically > 1e9)
                    const isSessionTime = typeof dateValue === 'number' && dateValue < 1000;

                    if (isSessionTime) {
                        // Format as session time (minutes:seconds)
                        const minutes = Math.floor(dateValue / 60);
                        const seconds = Math.floor(dateValue % 60);
                        const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
                        return <g key={i}>
                            <text className="graphic_grid_text" x={it} y="0">{timeStr}</text>
                            <path className="graphic_grid" d={`M${it} 0 L${it} ${size.y - 21}`} transform="translate(0 15)"></path>
                        </g>
                    } else {
                        // Format as date - use the same logic as session list
                        let date: Date;
                        
                        try {
                            // Use the same date handling as in SessionList.tsx and FullSessionList.tsx
                            // The data structure should have a 'date' field that can be passed to new Date()
                            date = new Date(dateValue);
                        } catch (error) {
                            date = new Date(NaN);
                        }

                        // Check if the date is valid and reasonable (after year 2000)
                        if (Number.isNaN(date.getTime()) || date.getFullYear() < 2000) {
                            return <g key={i}>
                                <text className="graphic_grid_text" x={it} y="0">--.--.--</text>
                                <path className="graphic_grid" d={`M${it} 0 L${it} ${size.y - 21}`} transform="translate(0 15)"></path>
                            </g>
                        }

                        const year = date.getFullYear().toString().slice(-2);
                        const dateStr = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth()+1).toString().padStart(2, '0')}.${year}`;
                        return <g key={i}>
                            <text className="graphic_grid_text" x={it} y="0">{dateStr}</text>
                            <path className="graphic_grid" d={`M${it} 0 L${it} ${size.y - 21}`} transform="translate(0 15)"></path>
                        </g>
                    }
                })}
                    {verticalLinesData.map((it, i)=>{
                        return <g key={`v${i}`}>
                            <text className="graphic_grid_text" x="0" y={size.y - (it * scaleY)}>{Math.round(it)}</text>
                            <path className="graphic_grid" d={`M20 ${size.y - (it * scaleY)} L${size.x} ${size.y - (it * scaleY)}`}></path>
                        </g>
                    })}
                </g>
                {showLast && <g transform={`translate(${(linesData[linesData.length-1] || linesData[linesData.length])- 23} 50)`}>
                    <rect x={0} y={0} width="46" height="14" rx="3.51096" fill="white" stroke="#E4E4E9" strokeWidth={1}/>
                    <text className="graphic_grid_text graphic_grid_value" x={23} y={10} dx={0} dy={0}>{Math.floor(values[values.length -1])} {valueTitle}</text>
                </g>}
                {median!=undefined && <g transform={`translate(${(linesData[linesData.length-1] || linesData[linesData.length]) - 60} ${size.y - median * scaleY + 2})`}>
                    <rect x={-45} y={0} width="90" height="30" rx="3.51096" fill="#fff9" stroke="#E4E4E9" strokeWidth={1}/>
                    <text className="graphic_grid_text graphic_grid_value" x={0} y={10} dx={0} dy={0}>
                        <tspan x={0}>{`${Math.floor(median)} w/m`}</tspan>
                        <tspan className="graphic_text_small" x={0} dy={15}>{`average of all time`}</tspan>
                    </text>
                </g>}
                {over && <path className="graphic_path_end" d={`M${0} ${cursorPosition.vy} L${size.x} ${cursorPosition.vy}`}></path>}
                {over && <g transform={`translate(${cursorPosition.vx} 10)`}>
                    <rect x={0} y={0} width="46" height="14" rx="3.51096" fill="white" stroke="#E4E4E9" strokeWidth={1}/>
                    <text className="graphic_grid_text graphic_grid_value" x={23} y={10} dx={0} dy={0}>{valueAlias?.[Math.floor((size.y - cursorPosition.vy) / scaleY)] || Math.floor((size.y - cursorPosition.vy) / scaleY)} {valueTitle}</text>
                </g>}
                {over && false && <g transform={`translate(${0} ${cursorPosition.vy + 2})`}>
                    <rect x={0} y={0} width="46" height="14" rx="3.51096" fill="white" stroke="#E4E4E9" strokeWidth={1}/>
                    <text className="graphic_grid_text graphic_grid_value" x={23} y={10} dx={0} dy={0}>{Math.floor(cursorPosition.vx)}</text>
                </g>}
            </g>
            <defs>
                <linearGradient id="fillGradient" gradientTransform="rotate(90)">
                    <stop offset="0%" stopColor="#FFA825" />
                    <stop offset="95%" stopColor="#FFA82500" />
                </linearGradient>
            </defs>
        </svg>
    </div>
}